import {
  StackActions,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useRef, useState } from 'react';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../components/AlertBox';
import { useGetStudyIntake } from '../../../hooks/study/useGetStudyIntake';
import { log } from '../../../tools/LogHelper';
import { FormElementProps } from '../../FormElement';
import { useGetFormTemplate } from '../../hooks/useGetFormTemplate';
import { FormElementPlugin } from '../FormElementPlugin';
import { useDynamicFormContext } from '../../DynamicFormContext';

type RedirectProps<T> = FormElementProps<T, RedirectConfig>;

export const Redirect = <T extends object>({
  element,
  stateNode
}: RedirectProps<T>) => {
  const {
    emitEvent,
    formValues,
    studyParticipantId: contextStudyParticipantId
  } = useDynamicFormContext<any, any>();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route = useRoute();
  const routeStudyParticipantId = (
    route.params as { studyParticipantId?: number }
  )?.studyParticipantId;
  const studyParticipantId =
    formValues?.studyParticipantId ??
    contextStudyParticipantId ??
    routeStudyParticipantId;
  const { redirectPath, redirectFormTemplateId, shouldLoadExistingFormData } =
    element.config || {};

  const { formTemplate, loadingFormTemplateError, getFormTemplate } =
    useGetFormTemplate(redirectFormTemplateId || 0);
  const [entityDataId, setEntityDataId] = useState<number>();
  const { studyIntake, loadingStudyIntakeError, getStudyIntake } =
    useGetStudyIntake();
  const [error, setError] = useState('');
  // Single-fire guard + cancellation for the redirect effect (CD-207). The
  // redirect's async work (loading existing entity data, then navigating) stays
  // hook-driven here; the AbortController replaces the previous handledRef
  // double-fire guard and lets us abort an in-flight redirect on unmount.
  const abortRef = useRef<AbortController | undefined>(undefined);
  // Latches once the 'final' event has been emitted. Kept separate from the
  // single-fire guard and never reset on error so a retry can re-run the
  // navigation without re-emitting 'final'.
  const finalEmittedRef = useRef(false);

  // Abort the in-flight redirect on unmount only — leaving the screen cancels
  // the pending navigation attempt.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (shouldLoadExistingFormData && formTemplate?.entity?.rules?.length) {
      const rule = formTemplate?.entity?.rules[0];
      if (rule.type === 'study-intake' && studyParticipantId) {
        // if intake form or study form then use studyParticipantId
        getStudyIntake(studyParticipantId);
      } else {
        // else error
        setError(
          'No existing data found. Currently only intake forms are supported.'
        );
      }
    }
  }, [
    formTemplate?.entity?.rules,
    getStudyIntake,
    shouldLoadExistingFormData,
    studyParticipantId
  ]);
  useEffect(() => {
    if (studyIntake) setEntityDataId(studyIntake.id);
  }, [studyIntake]);

  useEffect(() => {
    // Capture finality before any await; the machine can transition while the
    // redirect is in flight, so a post-await check could miss the 'final' emit.
    const wasFinal = stateNode?.type === 'final';
    const handleRedirect = async () => {
      if (
        !entityDataId &&
        redirectPath === 'FormTemplate' &&
        shouldLoadExistingFormData
      ) {
        // get form template and check entity rules
        getFormTemplate();
        return;
      }
      if (abortRef.current) return; // already redirecting / redirected
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        if (wasFinal && !finalEmittedRef.current) {
          await emitEvent('final', { transitions: [] });
          finalEmittedRef.current = true;
        }
        // the unmount cleanup may have aborted while 'final' was being
        // emitted — never navigate a screen that is gone
        if (controller.signal.aborted) return;
        if (redirectPath !== 'PreviousScreen') {
          if (redirectPath === 'FormTemplate') {
            navigation.push('FormTemplate', {
              formTemplateId: redirectFormTemplateId,
              studyParticipantId: studyParticipantId,
              entityDataId
            });
            return;
          } else if (redirectPath === 'MyData') {
            navigation.dispatch(
              StackActions.replace('DrawerNavigator', {
                screen: 'BottomTabNav',
                params: {
                  screen: 'MyDataNav'
                }
              })
            );
            return;
          } else if (redirectPath === 'StudiesNav') {
            navigation.dispatch(
              StackActions.replace('DrawerNavigator', {
                screen: 'BottomTabNav',
                params: {
                  screen: 'StudiesNav'
                }
              })
            );
            return;
          } else if (redirectPath === 'StudyComingSoon') {
            navigation.dispatch(
              StackActions.replace('DrawerNavigator', {
                screen: 'BottomTabNav',
                params: {
                  screen: 'StudiesNav',
                  params: {
                    screen: 'StudyComingSoon',
                    initial: false
                  }
                }
              })
            );
            return;
          } else if (redirectPath === 'ToDoNav') {
            navigation.dispatch(
              StackActions.replace('DrawerNavigator', {
                screen: 'BottomTabNav',
                params: {
                  screen: 'ToDoNav'
                }
              })
            );
            return;
          }
        }
        // default to the previous screen prior to the FormTemplate
        const navigationState = navigation.getState();
        let popCount = 0;
        for (let i = navigationState.routes.length - 1; i >= 0; i--) {
          if (
            navigationState.routes[i].name !== 'StudyEventForm' &&
            navigationState.routes[i].name !== 'FormTemplate'
          ) {
            navigation.pop(popCount);
            return;
          }
          popCount++;
        }
        // else navigate to My Chloe
        navigation.dispatch(
          StackActions.replace('DrawerNavigator', {
            screen: 'BottomTabNav',
            params: {
              screen: 'HomeNav'
            }
          })
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        // allow a retry on a later dep change. finalEmittedRef stays latched so
        // 'final' is not re-emitted if the retry re-runs after it was emitted.
        abortRef.current = undefined;
        setError('Unable to complete redirect. Please try again.');
        log(
          '[Redirect] navigation failed',
          err instanceof Error ? err.message : String(err)
        );
      }
    };
    // wait till next render loop since StudyEventFormDataSource doesn't
    // register the 'final' subscribe immediately
    const timeoutId = setTimeout(() => handleRedirect());
    return () => clearTimeout(timeoutId);
  }, [
    stateNode?.type,
    navigation,
    redirectPath,
    entityDataId,
    redirectFormTemplateId,
    shouldLoadExistingFormData,
    getFormTemplate,
    emitEvent,
    studyParticipantId
  ]);

  return error ? (
    <AlertBox severity='error'>{error}</AlertBox>
  ) : loadingFormTemplateError ? (
    <AlertBox severity='error'>{loadingFormTemplateError.message}</AlertBox>
  ) : loadingStudyIntakeError ? (
    <AlertBox severity='error'>{loadingStudyIntakeError.message}</AlertBox>
  ) : (
    <ActivityIndicatorScreen backgroundColor='transparent' />
  );
};

interface RedirectConfig {
  redirectPath?: string;
  redirectFormTemplateId?: number;
  shouldLoadExistingFormData?: boolean;
}

export const RedirectPlugin: FormElementPlugin<any, RedirectConfig> = {
  name: 'Redirect',
  type: 'Redirect',
  element: Redirect as React.FC<FormElementProps<any, RedirectConfig>>
};
